/**
 * ONCALL-SCHED-01a — the on-call pair as a chip, straight from the published
 * calendar. Today's header shows tonight's pair (or right now, after hours);
 * the Board day header shows the pair for the day on screen. Tap → Team →
 * On-Call. Renders nothing until a period is published.
 */
import { Link } from "react-router-dom";
import { AlertTriangle, Moon } from "lucide-react";
import { cn } from "@/lib/cn";
import { useAllActiveTechs } from "@/features/board/api";
import { availOutLabel, type AvailEvent } from "@/features/board/availability";
import { useOncallForDate, useOncallNow, useOncallHours, useOncallGaps, oncallPhase, techFirstName, fmtShort, fmtWeekday, isoDate, addDaysISO } from "./oncall";

function Chip({ primary, backup, lead, title, className }: { primary: string; backup: string | null; lead: string; title: string; className?: string }) {
  return (
    <Link
      to="/team?pane=oncall"
      title={title}
      className={cn(
        "inline-flex h-8 max-w-full items-center gap-1.5 rounded-full border border-hairline bg-white px-3 text-[12.5px] font-semibold text-slate-700 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50",
        className,
      )}
    >
      <Moon className="h-3.5 w-3.5 text-slate-400" />
      <span className="text-slate-500">{lead}</span>
      <span className="inline-flex items-center gap-1 text-blue-700"><span className="h-1.5 w-1.5 rounded-full bg-blue-500" />{primary}</span>
      {backup ? (
        <span className="inline-flex items-center gap-1 text-orange-700"><span className="h-1.5 w-1.5 rounded-full bg-orange-500" />{backup}</span>
      ) : (
        <span className="text-slate-400">no backup</span>
      )}
    </Link>
  );
}

/** Office Today: "On call tonight · Camron · Jeremy". */
export function OncallTodayChip({ className }: { className?: string }) {
  const { data } = useOncallNow();
  const { open, close } = useOncallHours();
  if (!data) return null;
  const phase = oncallPhase(new Date(), open, close);
  return (
    <Chip
      lead={phase === "now" ? "On call now" : "On call tonight"}
      primary={data.primary_name}
      backup={data.backup_name}
      title={`On call for ${fmtShort(data.oncall_day)} — primary ${data.primary_name}${data.backup_name ? `, backup ${data.backup_name}` : ", no backup"}. Open the on-call calendar.`}
      className={className}
    />
  );
}

/** Board day header: the pair for the date on screen. */
export function OncallDateChip({ date, className }: { date: string; className?: string }) {
  const { data } = useOncallForDate(date);
  const techs = useAllActiveTechs().data ?? [];
  if (!data) return null;
  const nameOf = (e: string | null) => { if (!e) return null; const t = techs.find((x) => x.empno === e); return t ? techFirstName(t) : e; };
  const p = nameOf(data.primary_empno) ?? data.primary_empno;
  const b = nameOf(data.backup_empno);
  return (
    <Chip
      lead="On call"
      primary={p}
      backup={b}
      title={`On call ${fmtShort(date)} — primary ${p}${b ? `, backup ${b}` : ", no backup"}. Open the on-call calendar.`}
      className={className}
    />
  );
}

/**
 * ONCALL-SCHED-01b — the Coverage Gap card on the office Today: every
 * published on-call day in the next 60 days whose tech has approved time off
 * (red) or a request still waiting (amber). Each line opens that day's editor.
 * Renders nothing when every day is covered.
 */
export function CoverageGapCard() {
  const from = isoDate(new Date());
  const { data } = useOncallGaps(from, addDaysISO(from, 60));
  const gaps = data ?? [];
  if (gaps.length === 0) return null;
  const hard = gaps.filter((g) => g.status === "approved");
  const soft = gaps.filter((g) => g.status === "requested");
  return (
    <div className={cn("rounded-2xl border px-4 py-3 shadow-sm", hard.length ? "border-red-200 bg-red-50/70" : "border-amber-200 bg-amber-50/70")}>
      <div className="flex flex-wrap items-center gap-2">
        <AlertTriangle className={cn("h-4 w-4", hard.length ? "text-red-600" : "text-amber-600")} />
        <span className="text-[13px] font-bold text-slate-900">
          {hard.length > 0 ? `On-call coverage gap — ${hard.length === 1 ? "1 day needs" : `${hard.length} days need`} a swap` : `On-call at risk — ${soft.length === 1 ? "a time-off request is" : `${soft.length} time-off requests are`} waiting on an on-call day`}
        </span>
        <Link to="/team?pane=oncall" className="ml-auto text-[12.5px] font-semibold text-brand-700 hover:underline">Open the calendar</Link>
      </div>
      <ul className="mt-1.5 flex flex-wrap gap-1.5">
        {[...hard, ...soft].slice(0, 8).map((g) => (
          <li key={`${g.day}-${g.role}`}>
            <Link to={`/team?pane=oncall&day=${g.day}`}
              className={cn("inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-[12px] font-semibold ring-1 hover:bg-slate-50", g.status === "approved" ? "text-red-800 ring-red-200" : "text-amber-800 ring-amber-200")}>
              {fmtWeekday(g.day)} · {g.tech_name} ({g.role}) · {availOutLabel({ kind: g.kind as AvailEvent["kind"] })}{g.status === "requested" ? " · waiting" : ""}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
